import { Button } from "@narmi/design_system";
import { RouteObject, useNavigate, useParams, useLocation } from "react-router-dom";
import { TopBarPageContainer } from "../../components/PageContainer";
import { PermissionedRoutes, ValidatedRoutes } from "../../components/PermissionedRoutes";
import { useUserConfiguration } from "../../hooks/useUserConfiguration";
import { MULTIENTITY_AGGREGATION_ROUTES } from "../multientity_aggregation/routes";
import { useInstitutionContext } from "@hooks/institutionContext";

export const USER_ROUTES: RouteObject[] = [
  {
    index: true,
    async lazy() {
      const { UsersList } = await import("./users_list/UsersList");
      return {
        Component() {
          const { features, permissions } = useUserConfiguration();
          const { institution } = useInstitutionContext();
          const navigate = useNavigate();

          return (
            <TopBarPageContainer
              title={institution.institution_type === "credit_union" ? "Members" : "Customers"}
              action={
                <>
                  {(features.includes("online_banking") || features.includes("mobile_banking")) &&
                    permissions.includes("backoffice.debug_enrollment_verification") && (
                      <Button
                        size="xs"
                        kind="secondary"
                        onClick={() => {
                          window.location.href = "/enrollment_helper";
                        }}
                        label="Enrollment helper"
                      />
                    )}
                  {permissions.includes("backoffice.manually_enroll_user") && (
                    <Button
                      size="xs"
                      onClick={() => {
                        navigate("/users/add_user");
                      }}
                      label={
                        institution.institution_type === "credit_union"
                          ? "Add member"
                          : "Add customer"
                      }
                    />
                  )}
                </>
              }
            >
              <UsersList />
            </TopBarPageContainer>
          );
        },
      };
    },
  },
  {
    path: ":userId",
    async lazy() {
      const { User } = await import("./user/User");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <User userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/ai_search",
    element: (
      <ValidatedRoutes
        validate={(userConfiguration) => {
          const { isFeatureEnabled, permissions } = userConfiguration;
          return (
            isFeatureEnabled("command_ai_search", "customer_profile") &&
            permissions.includes("backoffice.use_ai_search")
          );
        }}
      />
    ),
    children: [
      {
        index: true,
        async lazy() {
          const { AiSearch } = await import("./user/ai_search/AiSearch");
          return {
            Component() {
              const { userId } = useParams<{ userId: string }>();
              return <AiSearch userId={userId!} />;
            },
          };
        },
      },
    ],
  },
  {
    path: ":userId/ai_search/:threadId",
    element: (
      <ValidatedRoutes
        validate={(userConfiguration) => {
          const { isFeatureEnabled, permissions } = userConfiguration;
          return (
            isFeatureEnabled("command_ai_search", "customer_profile") &&
            permissions.includes("backoffice.use_ai_search")
          );
        }}
      />
    ),
    children: [
      {
        index: true,
        async lazy() {
          const { CustomerProfileAiThread } = await import("./user/ai_search/AiThread");
          return {
            Component() {
              const { userId } = useParams<{ userId: string }>();
              return <CustomerProfileAiThread userId={userId!} />;
            },
          };
        },
      },
    ],
  },
  {
    path: ":userId/security",
    async lazy() {
      const { UserSecurity } = await import("./user_security/UserSecurity");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <UserSecurity userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/security/phone",
    async lazy() {
      const { Phone } = await import("./user_security/Phone");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <Phone userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/ach_transfers",
    async lazy() {
      const { AchTransfers } = await import("./ach_transfers/AchTransfers");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <AchTransfers userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/ach_payments",
    async lazy() {
      const { AchPayments } = await import("./ach_payments/AchPayments");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <AchPayments userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/fednow",
    async lazy() {
      const { FedNowPayments } = await import("./fednow/FedNowPayments");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <FedNowPayments userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/wires",
    async lazy() {
      const { WireTransfers } = await import("./wires/WireTransfers");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <WireTransfers userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/bulk_payments",
    async lazy() {
      const { BulkPayments } = await import("./bulk_payments/BulkPayments");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <BulkPayments userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/m2m",
    async lazy() {
      const { Member2MemberTable } = await import("./m2m/Member2MemberTable");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <Member2MemberTable userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/alerts",
    async lazy() {
      const { AlertsTable } = await import("./alerts/AlertsTable");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <AlertsTable userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/bulk_payments/:bulkPaymentId/:paymentId?",
    async lazy() {
      const { BulkPaymentDetail } = await import("./bulk_payments/BulkPaymentDetail");
      return {
        Component() {
          const { userId, bulkPaymentId, paymentId } = useParams<{
            userId: string;
            bulkPaymentId: string;
            paymentId?: string;
          }>();
          return (
            <BulkPaymentDetail
              userId={userId!}
              bulkPaymentId={bulkPaymentId!}
              paymentId={paymentId}
            />
          );
        },
      };
    },
  },
  {
    path: ":userId/external_accounts",
    async lazy() {
      const { ExternalAccounts } = await import("./external_accounts/ExternalAccounts");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          const location = useLocation();
          const queryParams = new URLSearchParams(location.search);
          const accountId = queryParams.get("accountId");
          const membershipId = queryParams.get("membershipId");
          return (
            <ExternalAccounts
              userId={userId!}
              selectedAccountId={accountId}
              membershipId={membershipId}
            />
          );
        },
      };
    },
  },
  {
    path: ":userId/account/:accountId",
    async lazy() {
      const { Account } = await import("./account/Account");
      return {
        Component() {
          const { userId, accountId } = useParams<{ userId: string; accountId: string }>();
          const location = useLocation();
          const queryParams = new URLSearchParams(location.search);
          const tab = queryParams.get("tab");
          const selectedId = queryParams.get("selectedId");
          const page = queryParams.get("page") ?? "1";
          return (
            <Account
              userId={userId!}
              accountId={accountId!}
              selectedTab={tab}
              selectedId={selectedId}
              page={page}
            />
          );
        },
      };
    },
  },
  {
    path: ":userId/multientity_aggregation/:orgId",
    element: <PermissionedRoutes requiredFeatures={["multientity_aggregation"]} />,
    children: MULTIENTITY_AGGREGATION_ROUTES,
  },
  {
    element: <PermissionedRoutes requiredPermissions={["backoffice.manually_enroll_user"]} />,
    children: [
      {
        path: "add_user",
        async lazy() {
          const { AddUserContainer } = await import("./add_user/AddUser");
          const { AddUserContainerV2 } = await import("./add_user/AddUserV2");
          return {
            Component() {
              const { institution } = useInstitutionContext();
              const navigate = useNavigate();
              const { features } = useUserConfiguration();
              return (
                <TopBarPageContainer
                  title={
                    institution.institution_type === "credit_union" ? "Add member" : "Add customer"
                  }
                  backIconOnClick={() => navigate("/users")}
                >
                  {features.includes("add_user_redesign") ? (
                    <AddUserContainerV2 />
                  ) : (
                    <AddUserContainer />
                  )}
                </TopBarPageContainer>
              );
            },
          };
        },
      },
    ],
  },
  {
    path: ":userId/update_segment",
    element: <PermissionedRoutes requiredPermissions={["backoffice.update_segment"]} />,
    children: [
      {
        index: true,
        async lazy() {
          const { SegmentSelection } = await import("./update_segment/Selection");
          return {
            Component() {
              const { userId } = useParams<{ userId: string }>();
              return <SegmentSelection userId={userId!} />;
            },
          };
        },
      },
    ],
  },
  {
    path: ":userId/history",
    async lazy() {
      const { ActivityHistory } = await import("./history/ActivityHistory.tsx");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <ActivityHistory userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/linked_services",
    async lazy() {
      const { LinkedServices } = await import("./LinkedServices.tsx");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <LinkedServices userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/authorized_apps",
    async lazy() {
      const { AuthorizedApps } = await import("./AuthorizedApps");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <AuthorizedApps userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/features",
    async lazy() {
      const { Features } = await import("./Features");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <Features userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/messages",
    async lazy() {
      const { UserMessages } = await import("./messages/UserMessages");
      const { UserMessagesV2 } = await import("./messages_v2/UserMessagesV2.tsx");
      return {
        Component() {
          const { features } = useUserConfiguration();
          const { userId } = useParams<{ userId: string }>();
          return features.includes("secure_messages_redesign") ? (
            <UserMessagesV2 userId={userId!} />
          ) : (
            <UserMessages userId={userId!} />
          );
        },
      };
    },
  },
  {
    path: ":userId/messages/:threadId",
    async lazy() {
      const { UserThreadDetails } = await import("./messages/UserThreadDetails");
      const { UserThreadDetailsV2 } = await import("./messages_v2/UserThreadDetailsV2.tsx");
      return {
        Component() {
          const { features } = useUserConfiguration();
          const { userId, threadId } = useParams<{ userId: string; threadId: string }>();
          return features.includes("secure_messages_redesign") ? (
            <UserThreadDetailsV2 userId={userId!} threadId={threadId!} />
          ) : (
            <UserThreadDetails userId={userId!} threadId={threadId!} />
          );
        },
      };
    },
  },
  {
    path: ":userId/security/authenticator",
    async lazy() {
      const { Authenticator } = await import("./user_security/Authenticator");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <Authenticator userId={userId!} />;
        },
      };
    },
  },
  {
    path: ":userId/security/recovery",
    async lazy() {
      const { RecoveryCodes } = await import("./user_security/RecoveryCodes");
      return {
        Component() {
          const { userId } = useParams<{ userId: string }>();
          return <RecoveryCodes userId={userId!} />;
        },
      };
    },
  },
];
